import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';

const toolName = 'search-variables' as const;

const argumentZod = z.object({
  offset: z
    .number()
    .optional()
    .describe('Pagination offset for large variable sets (default: 0)'),
  collections: z
    .array(z.string())
    .optional()
    .describe(
      'Filter to specific collection names (case-insensitive). If omitted, returns all collections'
    ),
  types: z
    .array(z.enum(['color', 'length', 'font-family', 'number', 'percentage']))
    .optional()
    .describe(
      'Filter to specific variable types. If omitted, returns all types'
    ),
  nameContains: z
    .string()
    .optional()
    .describe(
      'Search for variables whose NAME contains this string (case-insensitive). Use for variable naming conventions like "brand", "primary", "heading". Do NOT use for color searches - use colorMatch instead.'
    ),
  colorMatch: z
    .string()
    .optional()
    .describe(
      'Find colors by perceptual similarity. MUST be a hex code (e.g., "#FF0000"). Optional threshold: "#FF0000@10" (default: 40, lower = stricter).'
    ),
});

export const searchVariablesManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Search variables',
  family: 'pages',
  prettyStatus: {
    executed: 'Searched variables',
    executing: 'Searching variables',
    failed: 'Failed to search variables',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Retrieves CSS variables from the current site.

      Returns JSON with:
      - metadata: collection names, pagination info, applied filters
      - variables: CSS string with variable definitions by collection/mode

      Filters (all combinable):
      - collections: Filter by collection name (case-insensitive)
      - types: "color", "length", "font-family", "number", "percentage"
      - nameContains: Search variable names (e.g., "brand", "primary")
      - colorMatch: Find similar colors by hex (e.g., "#FF0000" or "#FF0000@10" for stricter matching)

      Pagination: offset (default 0), returns up to 100 per call.

      Use colorMatch for color searches, nameContains for variable name searches.
    `,
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [{resource: 'SiteVariable', action: 'manage'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
  timeout: 10000,
});
